import useAnalyticsCalls from '@/hooks/use-analytics';
import useIsMobile from '@/hooks/use-is-mobile';
import { Box, Flex, IconDownload, Link, Text } from '@cmpsr/components';
import { useState } from 'react';
import { catchBackendApiClient } from '@/client/customFetcher';

export interface BillingReceiptProps {
  subscriptionId: string;
}

/**
 * BillingReceipt component - Provides download functionality for subscription receipts
 *
 * This component allows users to download PDF receipts for their subscription payments.
 * The backend handles all receipt generation/retrieval and returns a PDF binary blob.
 */
export function BillingReceipt({ subscriptionId }: BillingReceiptProps) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const analytics = useAnalyticsCalls();
  const isMobile = useIsMobile();
  const size = isMobile ? 's' : 'm';

  const handleDownload = async () => {
    analytics.callReceiptDownloaded(subscriptionId);
    setError(null);

    try {
      setIsDownloading(true);

      // Call the API with responseType: 'blob' (same pattern as doctor letter download)
      const response = await catchBackendApiClient.get(
        `/v1/subscriptions/${subscriptionId}/receipt`,
        {
          responseType: 'blob',
        }
      );

      // Trigger browser download
      const blob = response.data as Blob;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Extract filename from Content-Disposition header if available
      // Backend provides correct invoice number in filename
      const contentDisposition = response.headers['content-disposition'];
      let filename = `catch-receipt-${subscriptionId}.pdf`; // fallback

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+?)"/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }

      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (err) {
      // Handle different error scenarios
      let errorMessage = 'Failed to download receipt';

      if (err instanceof Error) {
        // Check for common error cases
        if (err.message.includes('404')) {
          errorMessage = 'No receipt available yet. Receipts are generated after your first payment.';
        } else if (err.message.includes('403')) {
          errorMessage = 'Access denied';
        } else {
          errorMessage = err.message;
        }
      }

      setError(errorMessage);
      console.error('Receipt download failed:', err);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Flex
      flexDirection="column"
      p={{ base: 'spacer-4', md: 'spacer-6' }}
      border="1px solid"
      borderColor="ui-element-divider"
      borderRadius="radii-3"
      backgroundColor="background-light"
      gap="spacer-4"
    >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Text variant="text-header-M">Billing Receipt</Text>
        <Link
          as="button"
          onClick={handleDownload}
          variant="link-secondary"
          size={size}
          trailingIcon={<IconDownload />}
          disabled={isDownloading}
          aria-label="Download billing receipt PDF"
        >
          {isDownloading ? 'Downloading...' : 'Download'}
        </Link>
      </Box>

      {error && (
        <Box
          p="spacer-3"
          borderRadius="radii-2"
          backgroundColor="feedback-danger-subtle"
          color="feedback-danger-bold"
        >
          <Text variant="text-body-regular">{error}</Text>
        </Box>
      )}
    </Flex>
  );
}
